import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import App from "./App.tsx";
import "./index.css";
import { I18nProvider } from "./utils/i18n";
import { bizError, bizDebug } from "./utils/debugLogger";
import { AppErrorBoundary } from "./components/AppErrorBoundary";

// Register global error tracking
const isDev = (import.meta as any).env?.DEV;

if (isDev) {
  bizDebug("APP_SHELL_INIT", "Main script initialized", { timestamp: new Date().toISOString() });
}

const isViteHmrError = (message: any, errorOrReason: any) => {
  if (!isDev) return false;
  const msgText = String(message || "").toLowerCase();
  const errText = errorOrReason ? String(errorOrReason.message || errorOrReason).toLowerCase() : "";
  const stackText = errorOrReason?.stack ? String(errorOrReason.stack).toLowerCase() : "";
  
  return (
    msgText.includes("websocket closed without opened") ||
    errText.includes("websocket closed without opened") ||
    stackText.includes("websocket closed without opened") ||
    msgText.includes("failed to connect to websocket") ||
    errText.includes("failed to connect to websocket") ||
    stackText.includes("failed to connect to websocket") ||
    msgText.includes("/@vite/client") ||
    errText.includes("/@vite/client") ||
    stackText.includes("/@vite/client") ||
    msgText.includes("vite") ||
    errText.includes("vite") ||
    stackText.includes("vite")
  );
};

const isChunkLoadError = (err: any) => {
  const str = String(err?.message || err?.reason || err || "").toLowerCase();
  return (
    str.includes("failed to fetch dynamically imported module") ||
    str.includes("loading chunk") ||
    str.includes("loading css chunk") ||
    str.includes("importing a module script failed") ||
    str.includes("error loading module")
  );
};

const handleChunkOrVersionError = (error: any) => {
  if (isChunkLoadError(error)) {
    console.warn("[Chunk Load Error Detected]", error);
    try {
      const lastRetry = Number(sessionStorage.getItem("biz_chunk_retry_timestamp") || "0");
      const now = Date.now();
      if (now - lastRetry > 10000) {
        sessionStorage.setItem("biz_chunk_retry_timestamp", now.toString());
        console.info("[Chunk Recovery] Auto-reloading page to fetch latest application assets...");
        window.location.reload();
        return true;
      }
    } catch {}
  }
  return false;
};

window.onerror = (message, source, lineno, colno, error) => {
  if (isViteHmrError(message, error)) {
    console.debug("[Vite HMR Error Ignored in Dev]", message);
    return;
  }
  if (handleChunkOrVersionError(error || message)) {
    return;
  }
  console.error("[GLOBAL_RUNTIME_ERROR]", { message, source, lineno, colno, error });
  bizError("GLOBAL_RUNTIME_ERROR", { message, source, lineno, colno, error: error?.stack || error });
};

window.onunhandledrejection = (event) => {
  if (isViteHmrError(event.reason?.message || event.reason, event.reason)) {
    console.debug("[Vite HMR Promise Rejection Ignored in Dev]", event.reason);
    return;
  }
  if (handleChunkOrVersionError(event.reason)) {
    return;
  }
  console.error("[UNHANDLED_PROMISE_REJECTION]", event.reason);
  bizError("UNHANDLED_PROMISE_REJECTION", event.reason?.stack || event.reason);
};

// HARD OAuth hash redirect guard.

// Supabase sometimes returns to "/" with #access_token=...
// Move that hash to /auth/callback before React renders the login screen.
const hasOAuthHash =
  window.location.hash.includes("access_token=") ||
  window.location.hash.includes("refresh_token=") ||
  window.location.hash.includes("error=");

if (hasOAuthHash && !window.location.pathname.startsWith("/auth/callback")) {
  window.location.replace(`/auth/callback${window.location.hash}`);
} else {
  const rootElement = document.getElementById("root");
  if (rootElement) {
    try {
      const root = createRoot(rootElement);
      root.render(
        <StrictMode>
          <AppErrorBoundary>
            <I18nProvider>
              <App />
            </I18nProvider>
          </AppErrorBoundary>
        </StrictMode>
      );
      (window as any).__APP_MOUNTED__ = true;
      if (isDev) {
        bizDebug("APP_SHELL_MOUNTED", "React app shell successfully mounted", { timestamp: new Date().toISOString() });
      }
    } catch (renderError) {
      console.error("[CRITICAL_MOUNT_ERROR]", renderError);
      bizError("CRITICAL_MOUNT_ERROR", renderError);
      rootElement.innerHTML = `
        <div style="min-height:100vh;display:flex;align-items:center;justify-content:center;background:#0f172a;color:#f8fafc;padding:2rem;font-family:sans-serif;">
          <div style="max-width:480px;width:100%;background:#1e293b;border:1px solid #334155;border-radius:12px;padding:2rem;text-align:center;">
            <h1 style="font-size:1.5rem;font-weight:700;margin-bottom:0.75rem;">A new version is available</h1>
            <p style="color:#94a3b8;font-size:0.95rem;margin-bottom:1.5rem;">BizNearYou was updated. Please refresh your browser to load the latest release.</p>
            <button onclick="window.location.reload(true)" style="background:#2563eb;color:#fff;font-weight:600;padding:0.6rem 1.25rem;border-radius:8px;border:none;cursor:pointer;">Reload Page</button>
          </div>
        </div>
      `;
    }
  }
}